/* ============================================================
 * Holy Grail Nail — Cart page controller.
 *
 * Paired with sections/hgn-cart-page.liquid +
 * snippets/hgn-cart-page-line.liquid + assets/hgn-cart-page.css.
 *
 * SCOPE
 * -----
 * The section renders the cart server-side; this controller only
 * handles the live bits — qty/remove, the reservation timer mount,
 * the savings hydration, the free-shipping bar, and the Checkout
 * bridge. All mutations go through Shopify's Section Rendering API
 * (`sections: ['hgn-cart-page']`), so re-render is "fetch HTML,
 * swap node" — no client-side templating, no drift from cart state.
 *
 * CROSS-SURFACE SYNC
 * ------------------
 * Mutations on this page fire `cart:updated` + `hgn:cart:change` so
 * the side cart re-fetches and re-renders. Mutations from the side
 * cart (and any other surface) fire `hgn:cart:change`, which this
 * controller listens to → it re-runs the section fetch so the cart
 * page picks up changes made elsewhere.
 *
 * RE-BOOT ON SECTION REPLACE
 * --------------------------
 * Section Rendering API replaces the entire section node, including
 * the `<script src="hgn-cart-page.js">` tag. The browser does NOT
 * re-execute external scripts whose src is already in the document,
 * so we rebind the new section via `init()` from the post-mutation
 * response handler, not by relying on the inline script tag.
 * ============================================================ */

(function () {
  'use strict';

  var SECTION_SELECTOR = '[data-hgn-cart-page]';
  /* The `sections` parameter passed to /cart/change.js + /cart/update.js
     is the section's FILE NAME (without `.liquid`), matching how
     Shopify's Section Rendering API keys its response. */
  var SECTION_NAME = 'hgn-cart-page';

  /* ─── Boot ─────────────────────────────────────────────────── */
  function boot() {
    var section = document.querySelector(SECTION_SELECTOR);
    if (section) init(section);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* ─── Per-section init (idempotent) ────────────────────────── */
  function init(section) {
    if (section.__hgnBound) return;
    section.__hgnBound = true;

    [mountTimer, bindCheckout, bindQtyHandlers, bindLineRemove,
     bindBundleRemove, bindAdHocPanel, bindBeansPanel, hydrateShipbar,
     hydrateBundleCurrency].forEach(function (step) {
      /* Isolated: one throwing hydrator must not strand the rest —
         pre-B6 a TypeError in hydrateShipbar also killed
         listenForExternalCartChanges (page stopped reacting to the
         side cart entirely). */
      try { step(section); } catch (e) {
        if (window.console) console.warn('[hgn-cart-page] init step failed:', step.name, e);
      }
    });
    /* Savings + subtotal are server-rendered from the section's own
       savings computation block — no JS hydration needed and none
       wanted (it would clobber the correct figures with stale values
       if the side cart's `_render` hasn't run yet on this page load). */
    listenForExternalCartChanges(section);
    /* Re-run the currency hydrate when live FX rates arrive (they
       land async via hgn-currency's fetch) so non-GBP figures upgrade
       from fallback to live rates. Section swaps re-init and rebind. */
    document.addEventListener('hgn:fx:update', function () {
      try { hydrateBundleCurrency(section); } catch (e) {}
    });
  }

  /* ─── Reservation timer mount ──────────────────────────────── */
  function mountTimer(section) {
    var host  = section.querySelector('[data-hgn-cart-page-timer]');
    var title = section.querySelector('.hgn-cart-page__title');
    if (!host || !title) return;
    if (!window.HGN || !window.HGN.cartTimer) {
      /* cartTimer is defined by the side-cart snippet, which loads
         after this asset on slow parses — retry briefly instead of
         silently never mounting (B6). */
      if (!section.__hgnTimerRetrying) {
        section.__hgnTimerRetrying = true;
        retryShortly(function () { mountTimer(section); });
      }
      return;
    }
    section.__hgnTimerRetrying = false;
    /* Alternate mode mirrors the side cart — title ↔ timer cross-fade
       every 5s while a reservation is live. The reservation itself is
       started by the side cart's _render on first cart add and shared
       across surfaces via localStorage, so we don't call
       ensureStarted() here. */
    window.HGN.cartTimer.mount(host, { alternateWith: title });
  }

  /* ─── Checkout — bridges to the side cart's mint flow ──────── */
  function bindCheckout(section) {
    var btn = section.querySelector('[data-hgn-cart-page-checkout]');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var sc = window.HGN && window.HGN.sideCart;
      if (sc && typeof sc.proceedToCheckout === 'function') {
        sc.proceedToCheckout();
        return;
      }
      /* Fallback for the unlikely case that the side cart's
         controller hasn't registered yet — go straight to /checkout.
         Loses kit/gift/Beans savings, but better than a dead button. */
      window.location.href = '/checkout';
    });
  }

  /* ─── Qty stepper (+/-) ────────────────────────────────────── */
  function bindQtyHandlers(section) {
    section.addEventListener('click', function (e) {
      var dec = e.target.closest('[data-hgn-cart-page-qty-dec]');
      var inc = e.target.closest('[data-hgn-cart-page-qty-inc]');
      if (!dec && !inc) return;
      var line = e.target.closest('[data-hgn-cart-page-line]');
      if (!line || line.classList.contains('is-busy')) return;
      var valueEl = line.querySelector('[data-hgn-cart-page-qty-value]');
      var current = valueEl ? (parseInt(valueEl.textContent, 10) || 0) : 0;
      var next = inc ? current + 1 : Math.max(0, current - 1);
      if (next === current) return;
      setLineBusy(line, true);
      changeLine(line.getAttribute('data-line-key'), next, line);
    });
  }

  /* ─── Per-line remove (X button) ───────────────────────────── */
  function bindLineRemove(section) {
    section.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-hgn-cart-page-line-remove]');
      if (!btn) return;
      var line = btn.closest('[data-hgn-cart-page-line]');
      if (!line || line.classList.contains('is-busy')) return;
      setLineBusy(line, true);
      changeLine(line.getAttribute('data-line-key'), 0, line);
    });
  }

  /* ─── Per-bundle remove (whole kit) ────────────────────────── */
  function bindBundleRemove(section) {
    section.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-hgn-cart-page-bundle-remove]');
      if (!btn) return;
      var bundle = btn.closest('[data-hgn-cart-page-bundle]');
      if (!bundle || bundle.classList.contains('is-busy')) return;
      /* TODO (phase 2): replace native confirm() with the side cart's
         hgn-bundle-remove-modal so the "Move designs to cart / Delete
         kit / Cancel" affordances are consistent across surfaces. */
      var ok = window.confirm('Remove this Starter Kit from your cart?');
      if (!ok) return;
      var keysCsv = bundle.getAttribute('data-bundle-line-keys') || '';
      var keys = keysCsv.split(',').filter(function (k) { return !!k; });
      if (!keys.length) return;
      bundle.classList.add('is-busy');
      var updates = {};
      keys.forEach(function (k) { updates[k] = 0; });
      fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates: updates, sections: [SECTION_NAME] })
      })
        .then(handleSectionMutationResponse)
        .catch(function (err) {
          if (window && window.console) console.warn('[hgn-cart-page] bundle remove failed:', err);
          bundle.classList.remove('is-busy');
        });
    });
  }

  function changeLine(key, quantity, lineEl) {
    if (!key) return Promise.resolve();
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: [SECTION_NAME] })
    })
      .then(handleSectionMutationResponse)
      .catch(function (err) {
        if (window && window.console) console.warn('[hgn-cart-page] change failed:', err);
        /* Un-stick the row — a network failure used to leave is-busy
           set forever, freezing the +/− and remove controls (B6). */
        if (lineEl) setLineBusy(lineEl, false);
      });
  }

  /* Shared post-mutation handler: swap the section node from the
     response payload and fire the cross-surface cart-change events so
     the side cart re-fetches. */
  function handleSectionMutationResponse(res) {
    if (!res) return null;
    var contentType = (res.headers.get && res.headers.get('content-type')) || '';
    /* Shopify returns HTML for rate-limit (429) error pages — guard
       against res.json() throwing on those. */
    if (contentType.indexOf('text/html') !== -1) {
      fireCartChange();
      return null;
    }
    return res.json().then(function (data) {
      if (data && data.sections && typeof data.sections[SECTION_NAME] === 'string') {
        replaceSection(data.sections[SECTION_NAME]);
      }
      fireCartChange();
      return data;
    }).catch(function () {
      fireCartChange();
      return null;
    });
  }

  function replaceSection(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newSection = doc.querySelector(SECTION_SELECTOR);
    var oldSection = document.querySelector(SECTION_SELECTOR);
    if (!newSection || !oldSection) return;
    oldSection.replaceWith(newSection);
    init(newSection);
  }

  function setLineBusy(line, busy) {
    if (busy) line.classList.add('is-busy');
    else line.classList.remove('is-busy');
  }

  function fireCartChange() {
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('hgn:cart:change', { bubbles: true }));
    } catch (e) {}
  }

  /* ─── Free-shipping progress bar ───────────────────────────── */
  function hydrateShipbar(section) {
    var bar  = section.querySelector('[data-hgn-cart-page-shipbar]');
    var line = section.querySelector('[data-hgn-cart-page-shipbar-line]');
    var fill = section.querySelector('[data-hgn-cart-page-shipbar-fill]');
    if (!bar || !line || !fill) return;

    var state = readStateIsland(section);
    if (!state || state.itemCount === 0) {
      bar.hidden = true;
      return;
    }
    var cfg = window.HGN_SIDE_CART_CONFIG;
    if (!cfg || !cfg.shipping) {
      /* Shipping config is hydrated by the announcement bar. If it
         hasn't landed yet, poll briefly — same pattern the side cart
         uses for its kitProgress dependency. */
      if (!section.__hgnShipbarRetrying) {
        section.__hgnShipbarRetrying = true;
        retryShortly(function () { hydrateShipbar(section); });
      }
      return;
    }

    var currencyKey = (state.currency || 'GBP').toLowerCase();
    var shipGbp = (cfg.shipping[currencyKey] != null) ? cfg.shipping[currencyKey] : (cfg.shipping.gbp || 40);
    var shipDisplay = convertGbpToCurrencyDisplay(shipGbp, currencyKey);
    var shipCents = Math.round(shipDisplay * 100);
    var subtotalCents = Math.max(0, state.subtotalCents || 0);
    var pct = shipCents > 0 ? Math.min(100, Math.round((subtotalCents / shipCents) * 100)) : 0;
    var unlocked = subtotalCents >= shipCents;

    fill.style.width = pct + '%';
    bar.classList.toggle('is-ship-unlocked', unlocked);
    if (unlocked) {
      line.innerHTML = '✨ Free shipping unlocked';
    } else {
      var remainingCents = shipCents - subtotalCents;
      line.innerHTML = 'Only <strong>' + formatMoney(remainingCents, state.currency) + '</strong> to free shipping';
    }
    bar.hidden = false;
  }

  function convertGbpToCurrencyDisplay(gbp, currencyKey) {
    if (currencyKey === 'gbp' || !window.HGN) return gbp;
    if (typeof window.HGN.formatLiveCeil === 'function') {
      try {
        var formatted = window.HGN.formatLiveCeil(gbp, currencyKey);
        var n = parseFloat(String(formatted).replace(/[^0-9.]/g, ''));
        if (!isNaN(n)) return n;
      } catch (e) {}
    }
    return gbp;
  }

  function formatMoney(cents, currency) {
    var amount = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return '£' + amount.toFixed(2);
    }
  }

  /* ─── Cross-surface sync — re-fetch section on external changes ── */
  function listenForExternalCartChanges(section) {
    /* Coalesce bursts (the side cart fires hgn:cart:change after every
       mutation and `cart:updated` is also dispatched for legacy
       listeners — a single mutation can pair-fire both). 200 ms
       trailing edge mirrors the side cart's own refresh debounce. */
    var pending = false;
    function refresh() {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        fetchAndReplace();
      }, 200);
    }
    document.addEventListener('hgn:cart:change', refresh);
    document.addEventListener('cart:updated', refresh);
  }

  function fetchAndReplace() {
    /* Fetch from /cart so Shopify renders this section in its native
       template context. `/?sections=` works in many setups but is the
       homepage's template context — flakier when the section reads
       cart.attributes that have just been written by another surface.
       /cart route is deterministic. */
    fetch('/cart?sections=' + SECTION_NAME, {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data && typeof data[SECTION_NAME] === 'string') {
          replaceSection(data[SECTION_NAME]);
        }
      })
      .catch(function () {});
  }

  /* ─── Ad-hoc discount panel ───────────────────────────────────
     Apply/Cancel route through window.HGN.sideCart.applyAdHoc /
     .cancelAdHoc so the inspect → intent-attribute → mint flow stays
     single-sourced in the side cart's controller. The side cart's
     handler is fire-and-forget (no Promise return) so we surface
     errors by polling sideCart.lastAdHocError() ~1.5 s later — if
     the apply succeeded, `hgn:cart:change` already swapped this
     section to the applied-state markup before the timer fires. */
  function bindAdHocPanel(section) {
    var idleApply = section.querySelector('[data-hgn-cart-page-adhoc-apply]');
    var idleInput = section.querySelector('[data-hgn-cart-page-adhoc-input]');
    var errorEl   = section.querySelector('[data-hgn-cart-page-adhoc-error]');
    var cancelBtn = section.querySelector('[data-hgn-cart-page-adhoc-cancel]');

    if (idleApply && idleInput) {
      var origLabel = idleApply.textContent;

      function showError(msg) {
        if (!errorEl) return;
        errorEl.textContent = msg;
        errorEl.hidden = false;
      }
      function clearError() {
        if (!errorEl) return;
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      function doApply() {
        if (idleApply.disabled) return;
        var code = String(idleInput.value || '').trim();
        if (!code) {
          showError('Enter a code to apply.');
          return;
        }
        var sc = window.HGN && window.HGN.sideCart;
        if (!sc || typeof sc.applyAdHoc !== 'function') {
          showError('Cart drawer not ready — please refresh and try again.');
          return;
        }
        clearError();
        idleApply.disabled = true;
        idleInput.disabled = true;
        idleApply.textContent = 'Checking…';

        /* Settle on whichever fires first:
           - `hgn:cart:change` (success — the side cart's apply path
             wrote cart attributes and dispatched the event; the
             cross-surface refetch listener will re-render this whole
             panel to applied-state markup as a sibling effect).
           - 2 s timeout (failure — `_handleAdHocApply` short-circuited
             with an inline error in the drawer; no cart change
             happens, so no event fires).
           Reset the in-place idle controls either way so the button
           never sits permanently on "Checking…" if the cross-surface
           section replace fails or lands later than the user expects.
           Writes are no-ops if the section was already swapped out —
           the references point at a detached node. */
        var settled = false;
        function settle(viaChange) {
          if (settled) return;
          settled = true;
          document.removeEventListener('hgn:cart:change', onChange);
          clearTimeout(timer);
          var err = sc.lastAdHocError ? sc.lastAdHocError() : '';
          if (err) showError(err);
          if (idleApply) {
            idleApply.disabled = false;
            idleApply.textContent = origLabel;
          }
          if (idleInput) {
            idleInput.disabled = false;
            if (viaChange && !err) idleInput.value = '';
          }
        }
        function onChange() { settle(true); }
        document.addEventListener('hgn:cart:change', onChange);
        var timer = setTimeout(function () { settle(false); }, 2000);

        sc.applyAdHoc(code);
      }

      idleApply.addEventListener('click', doApply);
      idleInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); doApply(); }
      });
      idleInput.addEventListener('input', clearError);
    }

    if (cancelBtn) {
      cancelBtn.addEventListener('click', function () {
        var sc = window.HGN && window.HGN.sideCart;
        if (!sc || typeof sc.cancelAdHoc !== 'function') return;
        cancelBtn.disabled = true;
        /* Section re-renders to idle state on hgn:cart:change. */
        sc.cancelAdHoc();
      });
    }
  }

  /* ─── Nail Points (Beans) panel ───────────────────────────────
     Preview + cancel here, apply bounces to the side cart drawer
     (where the amount-selector lives). Idle-state copy is hydrated
     from window.HGN.sideCart.beansBalance() once the side cart's
     /api/beans/balance fetch completes — repaints on every
     `hgn:cart:change` so a balance arriving after section render is
     picked up. */
  function bindBeansPanel(section) {
    var cancelBtn = section.querySelector('[data-hgn-cart-page-beans-cancel]');
    var openBtn   = section.querySelector('[data-hgn-cart-page-beans-open]');
    var msgEl     = section.querySelector('[data-hgn-cart-page-beans-message]');

    if (cancelBtn) {
      cancelBtn.addEventListener('click', function () {
        var sc = window.HGN && window.HGN.sideCart;
        if (!sc || typeof sc.cancelNailPoints !== 'function') return;
        cancelBtn.disabled = true;
        sc.cancelNailPoints();
      });
    }
    if (openBtn) {
      openBtn.addEventListener('click', function () {
        var sc = window.HGN && window.HGN.sideCart;
        if (sc && typeof sc.open === 'function') sc.open();
      });
    }
    if (msgEl) {
      function hydrate() {
        var sc = window.HGN && window.HGN.sideCart;
        if (!sc || typeof sc.beansBalance !== 'function') return;
        var b = sc.beansBalance();
        if (!b) return;
        if (b.isGuest) return;   // server already rendered guest state
        if (typeof b.beans === 'number' && b.beans > 0 &&
            typeof b.gbpValue === 'number' && b.gbpValue > 0) {
          msgEl.innerHTML =
            '<strong>' + b.beans + ' Nail Points</strong> available — ' +
            'redeem up to <strong>' + formatGbpForShopper(b.gbpValue) + ' off</strong> ' +
            'in the cart drawer.';
        }
      }
      hydrate();
      document.addEventListener('hgn:cart:change', hydrate);
      retryShortly(hydrate);
    }
  }

  /* ─── Non-GBP currency hydration (B6) ──────────────────────────
     The section's Liquid renders the bundle "paid" figure from the
     GBP-pence target via `| money`, which stamps the PRESENTMENT
     symbol on a GBP magnitude — €10.00 shown for a £10 target — and
     subtracts GBP pence from presentment cents for the save pill and
     summary. Server-side Liquid has no live FX, so on non-GBP carts
     we rewrite those figures here from the data attributes, using the
     same live rates the side cart uses (window.HGN.fxRates). GBP
     carts are exact server-side and skipped. */
  var FX_FALLBACK = { usd: 1.25, eur: 1.15, cad: 1.70, aud: 1.95, nzd: 2.10, chf: 1.10 };
  function shopperCurrencyKey(section) {
    var state = readStateIsland(section);
    return String((state && state.currency) || 'GBP').toLowerCase();
  }
  function fxRateFor(key) {
    if (key === 'gbp') return 1;
    return (window.HGN && window.HGN.fxRates && window.HGN.fxRates[key]) || FX_FALLBACK[key] || 1;
  }
  function moneyFor(cents, key) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: key.toUpperCase() }).format(cents / 100);
    } catch (e) {
      return (key === 'gbp' ? '£' : '') + (cents / 100).toFixed(2);
    }
  }
  function formatGbpForShopper(gbpValue) {
    var section = document.querySelector('[data-hgn-cart-page]');
    var key = section ? shopperCurrencyKey(section) : 'gbp';
    if (key === 'gbp') return '£' + gbpValue.toFixed(2);
    return moneyFor(Math.round(gbpValue * 100 * fxRateFor(key)), key);
  }
  function hydrateBundleCurrency(section) {
    var key = shopperCurrencyKey(section);
    if (key === 'gbp') return;
    var rate = fxRateFor(key);
    var bundleSavingsTotal = 0;
    section.querySelectorAll('[data-hgn-bundle-price]').forEach(function (block) {
      var targetGbp = parseInt(block.getAttribute('data-target-gbp-cents'), 10) || 0;
      var oneUnit = parseInt(block.getAttribute('data-oneunit-cents'), 10) || 0;
      if (!targetGbp) return;
      var paidPresentment = Math.round(targetGbp * rate);
      var save = Math.max(0, oneUnit - paidPresentment);
      bundleSavingsTotal += save;
      var paidEl = block.querySelector('[data-hgn-bundle-paid]');
      if (paidEl) paidEl.textContent = moneyFor(paidPresentment, key);
      var rrpEl = block.querySelector('.hgn-cart-page__bundle-price-rrp');
      if (rrpEl) rrpEl.hidden = save === 0;
      var saveEl = block.parentElement ? block.parentElement.querySelector('[data-hgn-bundle-save]') : null;
      if (saveEl) {
        saveEl.hidden = save === 0;
        if (save > 0) saveEl.textContent = 'Save ' + moneyFor(save, key);
      }
    });
    /* Summary rows — recompute only the bundle component; the
       non-bundle terms (gift retail, ad-hoc preview, Shopify
       total_discount, NP) are already presentment numbers from the
       server. NP intent suppresses bundle savings entirely — skip. */
    var row = section.querySelector('[data-hgn-summary-savings]');
    if (!row) return;
    if ((row.getAttribute('data-intent') || '') === 'nailpoints') return;
    var nonBundleTotal = parseInt(row.getAttribute('data-nonbundle-total-cents'), 10) || 0;
    var nonBundleSynth = parseInt(row.getAttribute('data-nonbundle-synth-cents'), 10) || 0;
    var rawSubtotal = parseInt(row.getAttribute('data-raw-subtotal-cents'), 10) || 0;
    var total = bundleSavingsTotal + nonBundleTotal;
    var synthetic = bundleSavingsTotal + nonBundleSynth;
    var valueEl = row.querySelector('[data-hgn-summary-savings-value]');
    row.hidden = total <= 0;
    if (valueEl && total > 0) valueEl.textContent = moneyFor(total, key);
    var subEl = section.querySelector('[data-hgn-summary-subtotal]');
    if (subEl) subEl.textContent = moneyFor(Math.max(0, rawSubtotal - synthetic), key);
    /* NP applied-amount span (server renders the GBP figure). */
    var npSpan = section.querySelector('[data-hgn-np-gbp-amount]');
    if (npSpan) {
      var npGbp = parseFloat(npSpan.getAttribute('data-hgn-np-gbp-amount')) || 0;
      if (npGbp > 0) npSpan.textContent = moneyFor(Math.round(npGbp * 100 * rate), key);
    }
  }

  /* ─── Utilities ────────────────────────────────────────────── */
  function readStateIsland(section) {
    var el = section.querySelector('[data-hgn-cart-page-state]');
    if (!el) return null;
    try {
      var state = JSON.parse(el.textContent);
      /* The island emits currency as an ISO string since the B6 fix;
         cached pre-fix HTML emitted the whole Liquid currency OBJECT
         ({iso_code:...}), whose .toLowerCase() killed init. Normalise
         both shapes so mixed cache states can't throw. */
      if (state && state.currency && typeof state.currency === 'object') {
        state.currency = state.currency.iso_code || 'GBP';
      }
      state.currency = String(state.currency || 'GBP');
      return state;
    } catch (e) { return null; }
  }

  /* Retry an action up to ~1.6 s for late-arriving globals
     (HGN.sideCart, HGN_SIDE_CART_CONFIG). Stops as soon as the action
     succeeds (no return value needed — the action is idempotent). */
  function retryShortly(action) {
    var tries = 0;
    var handle = setInterval(function () {
      tries += 1;
      try { action(); } catch (e) {}
      if (tries >= 20) clearInterval(handle);
    }, 80);
  }
})();
